import '../styles/amplify.css'
import '../styles/global.css'
// eslint-disable-next-line import/no-unresolved
import '@aws-amplify/ui-react/styles.css'

import {
  EditOutlined,
  FileDoneOutlined,
  PayCircleOutlined,
  QrcodeOutlined,
  ShoppingOutlined,
  SmileOutlined,
  UserOutlined,
} from '@ant-design/icons'
import { Route } from '@ant-design/pro-layout/lib/typing'
import { ApolloProvider } from '@apollo/client'
import {
  Authenticator,
  translations,
  useAuthenticator,
} from '@aws-amplify/ui-react'
import { ConfigProvider } from 'antd'
import locale from 'antd/locale/ja_JP'
import { Amplify } from 'aws-amplify'
import { I18n } from 'aws-amplify/utils'
import { AppProps } from 'next/app'
import Head from 'next/head'
import Router from 'next/router'
import React, { useEffect, useState } from 'react'

import { useApollo } from '../apollo/apolloClient'
import { BasicLayout } from '../components/layouts'
import Analytics from '../utils/Analytics'
import ErrorTracker from '../utils/ErrorTracker'

Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.NEXT_PUBLIC_USER_POOL_ID ?? '',
      userPoolClientId: process.env.NEXT_PUBLIC_USER_POOL_WEB_CLIENT_ID ?? '',
    },
  },
})

I18n.putVocabulariesForLanguage('ja', translations.ja)
I18n.setLanguage('ja')

const route: Route = {
  children: [
    {
      path: '/',
      name: 'トップページ',
      icon: <SmileOutlined />,
    },
    {
      name: '売上確認',
      icon: <PayCircleOutlined />,
      children: [
        {
          path: '/sales/summary',
          name: '売上サマリー',
        },
        {
          path: '/sales/daily_product_sales',
          name: '日別売上',
        },
        {
          path: '/sales/daily_ticket_usages',
          name: '日別チケット使用実績',
        },
      ],
    },
    {
      path: '/purchased_products',
      name: '購入実績',
      icon: <ShoppingOutlined />,
    },
    {
      path: '/products',
      name: '商品管理(β)',
      icon: <EditOutlined />,
    },
    {
      path: '/generate_qr',
      name: 'QRコード生成',
      icon: <QrcodeOutlined />,
    },
    {
      name: 'プレイス管理',
      icon: <FileDoneOutlined />,
      children: [
        {
          path: '/places',
          name: 'プレイス',
        },
        {
          path: '/place_tags',
          name: 'タグ',
        },
        {
          path: '/place_specs',
          name: '設備・サービス',
        },
      ],
    },
    {
      path: '/account_settings',
      name: 'アカウント',
      icon: <UserOutlined />,
    },
    // NOTE: HHCTではこれ以降の機能は不要なのでコメントアウト
    // {
    //   name: '定期券',
    //   icon: <IdcardOutlined />,
    //   routes: [
    //     {
    //       name: '定期券の一覧',
    //       path: '/simple_pass/my_passes',
    //     },
    //     {
    //       name: '売上確認',
    //       path: '/simple_pass/sales_summaries',
    //     },
    //     {
    //       name: '利用者属性',
    //       path: '/simple_pass/user_demographics',
    //     },
    //     {
    //       name: '購入実績',
    //       path: '/simple_pass/purchase_details',
    //     },
    //     {
    //       name: '利用者属性',
    //       path: '/simple_pass/user_demographics',
    //     },
    //   ],
    // },
    // {
    //   name: '分析(β)',
    //   icon: <BarChartOutlined />,
    //   routes: [
    //     {
    //       path: '/analytics/user_demographics',
    //       name: '利用者属性',
    //     },
    //     {
    //       path: '/analytics/ticket_usages',
    //       name: 'チケット利用',
    //     },
    //     {
    //       path: '/analytics/product_reviews',
    //       name: 'レビュー（商品）',
    //     },
    //     {
    //       path: '/analytics/ticket_reviews',
    //       name: 'レビュー（チケット）',
    //     },
    //   ],
    // },
    // {
    //   path: '/push_notifications',
    //   name: 'PUSH通知配信(β)',
    //   icon: <NotificationOutlined />,
    // },
  ],
}

const useRevision = () => {
  const [isOldRevision, setIsOldRevision] = useState(false)
  useEffect(() => {
    Router.events.on('routeChangeComplete', async () => {
      const currentRevision = process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA
      const result = await fetch('/revision.txt')
      const revision = await result.text()
      setIsOldRevision(currentRevision !== revision.trim())
    })
  }, [])

  return isOldRevision
}

const App = (props: AppProps) => (
  <Authenticator.Provider>
    <MyApp {...props} />
  </Authenticator.Provider>
)

const MyApp: React.FC<AppProps> = ({ Component, pageProps, router }) => {
  const apolloClient = useApollo()
  const { user: authUser, route: authRoute } = useAuthenticator()
  const user = { email: authUser?.signInDetails?.loginId ?? '' }
  ErrorTracker.setUser(user || null)

  const isOldRevision = useRevision()
  useEffect(() => {
    if (isOldRevision && process.env.NODE_ENV !== 'development') {
      window.location.reload()
    }
  }, [isOldRevision, router.pathname])

  useEffect(() => {
    Analytics.init()
    const handleRouteChange = (url: string) => {
      Analytics.logPageView(url)
    }
    Router.events.on('routeChangeComplete', handleRouteChange)
    return () => {
      Router.events.off('routeChangeComplete', handleRouteChange)
    }
  }, [])

  return authRoute === 'authenticated' && user ? (
    <ApolloProvider client={apolloClient}>
      <Head>
        <link
          rel="stylesheet"
          type="text/css"
          href="/iconFonts/ryde/css/ryde.css"
        />
        <link
          href="https://fonts.googleapis.com/icon?family=Material+Icons"
          rel="stylesheet"
        />
        <link
          href="https://cdn.jsdelivr.net/npm/@mdi/font@6.9.96/css/materialdesignicons.min.css"
          rel="stylesheet"
        />
      </Head>
      <ConfigProvider locale={locale}>
        <BasicLayout route={route}>
          <Component {...pageProps} />
        </BasicLayout>
      </ConfigProvider>
    </ApolloProvider>
  ) : (
    <Authenticator loginMechanisms={['email']} hideSignUp />
  )
}

export default App
